import { useQuery } from '@tanstack/react-query';
import { api, type MailRecipientOption } from './api';
import type { TranslationKey } from './i18n';

/**
 * The meeting calendar (operator, 2026-10-21): meetings one to one, for some
 * employees, for a department — or, for the administrators, everyone — held at
 * the office, outside it or online. Everyone asked accepts or declines; a
 * reminder goes Settings › Meetings' minutes before. Everyone sees their own; a
 * department's leader their people's too; an administrator all of them.
 */
/** GUEST: booked by someone from outside through its organizer's booking link (2026-11-05) — never picked on the form. */
export type MeetingAudience = 'ONE_TO_ONE' | 'PEOPLE' | 'DEPARTMENTS' | 'EVERYONE' | 'GUEST';
export type MeetingPlace = 'OFFICE' | 'OUTSIDE' | 'ONLINE';
export type MeetingStatus = 'SCHEDULED' | 'STARTED' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED';
export type MeetingAnswer = 'PENDING' | 'ACCEPTED' | 'DECLINED';
/** The reader's part in it: they called it, they are asked to it, or they see it as a leader or an administrator. */
export type MeetingRole = 'ORGANIZER' | 'INVITED' | 'VIEWER';

export const MEETING_AUDIENCES: MeetingAudience[] = ['ONE_TO_ONE', 'PEOPLE', 'DEPARTMENTS', 'EVERYONE'];
export const MEETING_PLACES: MeetingPlace[] = ['OFFICE', 'OUTSIDE', 'ONLINE'];

/**
 * Where a meeting stands, in order (operator, 2026-10-26: "add status to meeting
 * scheduled - started - in progress - done"). Whoever called it, or an
 * administrator, moves it forward, never back; the clock closes one still open
 * at its end as Done. Cancelled is beside the way, not on it.
 */
export const MEETING_STATUS_FLOW: MeetingStatus[] = ['SCHEDULED', 'STARTED', 'IN_PROGRESS', 'DONE'];

/** The API's limits (domain/meetings.ts). */
export const MAX_MEETING_PEOPLE = 300;
export const MAX_MEETING_AGENDA = 4000;
export const MAX_MEETING_LOCATION = 500;
export const MAX_MEETING_NOTE = 1000;
/** How long before its time a meeting may be started. */
export const EARLY_START_MINUTES = 30;

interface Person {
  id: string;
  fullName: string;
  fullNameAr: string | null;
}

interface Named {
  id: string;
  name: string;
  nameAr: string | null;
}

export interface AnswerCounts {
  accepted: number;
  declined: number;
  pending: number;
  total: number;
}

/** A calendar's row — `GET /meetings`. */
export interface MeetingRow {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string;
  place: MeetingPlace;
  location: string | null;
  status: MeetingStatus;
  audience: MeetingAudience;
  organizerId: string;
  organizer: Person;
  departments: Named[];
  role: MeetingRole;
  myAnswer: MeetingAnswer | null;
  answers: AnswerCounts;
}

export interface MeetingParticipant {
  user: Person & { jobTitle: string | null };
  answer: MeetingAnswer;
  answeredAt: string | null;
  /** Why they declined — only for them, whoever called it and the administrators. */
  note: string | null;
}

/** Whoever booked a meeting through a booking link (2026-11-05). */
export interface MeetingGuest {
  name: string;
  email: string;
  phone: string | null;
  company: string | null;
  note: string | null;
  locale: string;
  timezone: string;
  /** When they cancelled it themselves. */
  cancelledAt: string | null;
  createdAt: string;
}

/** `GET /meetings/:id`. */
export interface MeetingDetail extends Omit<MeetingRow, 'organizer'> {
  agenda: string | null;
  /** Who booked it, for a meeting booked through a booking link; null otherwise. */
  guest: MeetingGuest | null;
  organizer: Person & { jobTitle: string | null };
  participants: MeetingParticipant[];
  cancelledAt: string | null;
  cancelNote: string | null;
  cancelledBy: Person | null;
  /** When it was moved to its status, and who moved it: null for the clock that closed it at its end. */
  statusAt: string | null;
  statusBy: Person | null;
  createdAt: string;
  updatedAt: string;
  myNote: string | null;
  /** Settings › Meetings: how long before it the reminder goes; 0 for none. */
  reminderMinutes: number;
  /** `status`: the statuses the signed-in person may move it to now, in order. */
  can: { edit: boolean; cancel: boolean; answer: boolean; status: MeetingStatus[] };
}

/** `GET /meetings/options` — whom the form may ask. */
export interface MeetingOptions {
  anyone: boolean;
  /** Everyone but you, for whoever may call a meeting for everyone; null otherwise. */
  everyone: number | null;
  people: MailRecipientOption[];
  departments: Array<Named & { code: string; people: number }>;
  reminderMinutes: number;
}

/** `POST /meetings/busy` — of whoever the form would ask, who is busy then. */
export interface BusyPerson extends Person {
  you: boolean;
  meeting: boolean;
  leave: boolean;
}

/**
 * How a meeting looks to its reader on the calendar: cancelled, declined, waiting
 * for their answer, accepted, theirs (they called it), or someone else's they see.
 */
export type MeetingLook = 'cancelled' | 'declined' | 'pending' | 'accepted' | 'organizer' | 'viewer';

export function meetingLook(m: Pick<MeetingRow, 'status' | 'role' | 'myAnswer'>): MeetingLook {
  if (m.status === 'CANCELLED') return 'cancelled';
  if (m.role === 'ORGANIZER') return 'organizer';
  if (m.role === 'VIEWER') return 'viewer';
  if (m.myAnswer === 'DECLINED') return 'declined';
  if (m.myAnswer === 'ACCEPTED') return 'accepted';
  return 'pending';
}

/** A meeting's chip on the calendar, in the badges' tones: every one reads at 4.5:1 in both themes. */
export const LOOK_CHIP: Record<MeetingLook, string> = {
  organizer: 'bg-primary/12 text-primary-600 ring-primary/30 dark:text-primary-400',
  accepted: 'bg-success-bg text-success-fg ring-success/30 dark:bg-success/12 dark:text-success',
  pending: 'bg-warning-bg text-warning-fg ring-warning/40 dark:bg-warning/14 dark:text-warning-dark',
  declined: 'bg-neutral-500/10 text-[var(--text-secondary)] ring-neutral-500/25',
  viewer: 'bg-info-bg text-info-fg ring-info/30 dark:bg-info/12 dark:text-info-dark',
  cancelled: 'bg-neutral-500/10 text-[var(--text-tertiary)] ring-neutral-500/20 line-through',
};

/** The same, as a dot — a day on a phone's month, where a chip does not fit. */
export const LOOK_DOT: Record<MeetingLook, string> = {
  organizer: 'bg-primary',
  accepted: 'bg-success',
  pending: 'bg-warning',
  declined: 'bg-neutral-400',
  viewer: 'bg-info',
  cancelled: 'bg-neutral-500/50',
};

export const LOOK_LABEL: Record<MeetingLook, TranslationKey> = {
  organizer: 'meetings.look.organizer',
  accepted: 'meetings.look.accepted',
  pending: 'meetings.look.pending',
  declined: 'meetings.look.declined',
  viewer: 'meetings.look.viewer',
  cancelled: 'meetings.look.cancelled',
};

/**
 * How many invitations wait for the signed-in person's answer — the sidebar's count
 * beside Meeting Calendar. A new one rings the bell too, so this keeps the bell's pace.
 */
export function useWaitingMeetings(): number {
  const { data } = useQuery({
    queryKey: ['meetings', 'waiting-count'],
    queryFn: () => api.get<{ count: number }>('/meetings/waiting-count'),
    refetchInterval: 30_000,
  });
  return data?.count ?? 0;
}

// ---------------------------------------------------------------------------
// The calendar's days — the browser's own clock, as every date input is.
// ---------------------------------------------------------------------------

export const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
export const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
export const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
export const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);

/** The first day of the week `d` is in, the company's week starting on `weekStart` (0 Sunday … 6 Saturday). */
export const startOfWeek = (d: Date, weekStart: number) => addDays(startOfDay(d), -((d.getDay() - weekStart + 7) % 7));

const pad = (n: number) => String(n).padStart(2, '0');

/** "2026-10-22" — a local day, as a date input and the address keep it. */
export const dayKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** "10:30" — a local time, as a time input keeps it. */
export const timeKey = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** A day kept as "2026-10-22", or null for anything else. */
export function parseDayKey(key: string | null | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key ?? '');
  if (!match) return null;
  const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A local day and time as the instant they are: "2026-10-22" and "10:30". */
export function atTime(day: string, time: string): Date | null {
  const d = parseDayKey(day);
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!d || !match) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), Number(match[1]), Number(match[2]));
}

export const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** The days a month's page shows: whole weeks from the one its first day is in to the one its last day is in. */
export function monthDays(anchor: Date, weekStart: number): Date[] {
  const first = startOfWeek(startOfMonth(anchor), weekStart);
  const last = addDays(startOfMonth(addMonths(anchor, 1)), -1);
  const days: Date[] = [];
  for (let d = first; d <= last || days.length % 7 !== 0; d = addDays(d, 1)) days.push(d);
  return days;
}

/** Whether a meeting is on a day at all: it starts before the day ends and ends after it starts. */
export function onDay(m: { startsAt: string; endsAt: string }, day: Date): boolean {
  const start = startOfDay(day).getTime();
  const end = addDays(day, 1).getTime();
  return new Date(m.startsAt).getTime() < end && new Date(m.endsAt).getTime() > start;
}

/** Whole minutes from its start to its end. */
export const meetingMinutes = (m: { startsAt: string; endsAt: string }) =>
  Math.round((new Date(m.endsAt).getTime() - new Date(m.startsAt).getTime()) / 60_000);

/**
 * Where each meeting of a day sits on the week's time grid: those that overlap
 * share the width side by side, as a calendar lays them out. `column` of
 * `columns`, in the order they start.
 */
export function layoutDay<T extends { startsAt: string; endsAt: string }>(
  items: T[],
): Array<{ item: T; column: number; columns: number }> {
  const sorted = [...items].sort((a, b) => a.startsAt.localeCompare(b.startsAt) || b.endsAt.localeCompare(a.endsAt));
  const out: Array<{ item: T; column: number; columns: number }> = [];
  let cluster: Array<{ item: T; column: number; columns: number }> = [];
  let columnEnds: number[] = [];
  let clusterEnd = -Infinity;
  const close = () => {
    for (const placed of cluster) placed.columns = columnEnds.length;
    out.push(...cluster);
    cluster = [];
    columnEnds = [];
  };
  for (const item of sorted) {
    const start = new Date(item.startsAt).getTime();
    const end = new Date(item.endsAt).getTime();
    if (start >= clusterEnd && cluster.length) close();
    let column = columnEnds.findIndex((free) => free <= start);
    if (column === -1) {
      column = columnEnds.length;
      columnEnds.push(end);
    } else {
      columnEnds[column] = end;
    }
    cluster.push({ item, column, columns: 1 });
    clusterEnd = Math.max(cluster.length === 1 ? -Infinity : clusterEnd, end);
  }
  if (cluster.length) close();
  return out;
}

// ---------------------------------------------------------------------------
// Booking links (operator, 2026-11-05: "create a page with public link to sent to
// anyone to choose meeting also add setting to calendar to let any employee to add
// which time accept meetings"). The API's rules: domain/meeting-booking.ts.
// ---------------------------------------------------------------------------

/** One stretch of a weekday — 0 Sunday … 6 Saturday — in minutes from midnight, company time. */
export interface AvailabilityWindow {
  weekday: number;
  startMinute: number;
  endMinute: number;
}

/** `GET /meeting-booking/mine` — the signed-in person's booking page. */
export interface BookingPage {
  enabled: boolean;
  /** Settings › Meetings › Booking links: off, no link takes bookings. */
  linksOn: boolean;
  url: string;
  title: string | null;
  defaultTitle: { en: string; ar: string };
  intro: string | null;
  durationMinutes: number;
  bufferMinutes: number;
  noticeMinutes: number;
  daysAhead: number;
  place: MeetingPlace;
  location: string | null;
  windows: AvailabilityWindow[];
  /** The company's timezone — the hours are kept in it. */
  timezone: string;
  /** Meetings booked through it that are still to come. */
  upcoming: number;
  updatedAt: string;
}

export const BOOKING_DURATIONS = [15, 20, 30, 45, 60, 90, 120];
export const BOOKING_BUFFERS = [0, 5, 10, 15, 30, 45, 60];
export const BOOKING_NOTICES = [0, 30, 60, 120, 240, 480, 1440, 2880, 10080];
export const BOOKING_DAYS_AHEAD = [7, 14, 21, 30, 45, 60];
export const MAX_BOOKING_WINDOWS_A_DAY = 4;
export const MAX_BOOKING_TITLE = 80;
export const MAX_BOOKING_INTRO = 1000;

/** "10:30" from minutes after midnight; 1,440 is the day's end, which a time field writes as 00:00. */
export const minuteToTime = (minute: number) =>
  `${String(Math.floor((minute % 1440) / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;

/** Minutes after midnight from "10:30" — an end at 00:00 is the end of the day. */
export function timeToMinute(time: string, end = false): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match) return null;
  const minute = Number(match[1]) * 60 + Number(match[2]);
  return end && minute === 0 ? 1440 : minute;
}

/** "30 minutes", "1 hour 30 minutes", "2 days", "1 week" — and Arabic's own counting. */
export function bookingTimeText(minutes: number, locale: 'en' | 'ar'): string {
  const ar = locale === 'ar';
  if (minutes > 0 && minutes % 10080 === 0) {
    const w = minutes / 10080;
    return ar ? (w === 1 ? 'أسبوع' : w === 2 ? 'أسبوعان' : `${w} أسابيع`) : w === 1 ? '1 week' : `${w} weeks`;
  }
  if (minutes > 0 && minutes % 1440 === 0) {
    const d = minutes / 1440;
    return ar ? (d === 1 ? 'يوم' : d === 2 ? 'يومان' : `${d} أيام`) : d === 1 ? '1 day' : `${d} days`;
  }
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const hours = ar ? (h === 1 ? 'ساعة' : h === 2 ? 'ساعتان' : `${h} ساعات`) : h === 1 ? '1 hour' : `${h} hours`;
  const mins = ar ? (m <= 10 && m > 2 ? `${m} دقائق` : `${m} دقيقة`) : `${m} minutes`;
  if (h === 0) return mins;
  if (m === 0) return hours;
  return ar ? `${hours} و${mins}` : `${hours} ${mins}`;
}

/** "30 days" / «30 يومًا», «7 أيام». */
export function bookingDaysText(days: number, locale: 'en' | 'ar'): string {
  if (locale === 'ar') return days <= 10 ? `${days} أيام` : `${days} يومًا`;
  return days === 1 ? '1 day' : `${days} days`;
}

/**
 * What is wrong with one day's hours, stretch by stretch — the API's rules, so the
 * page says so before it saves: each ends after it starts, holds one meeting, and
 * does not overlap the one before it.
 */
export function dayHoursProblems(
  windows: AvailabilityWindow[],
  durationMinutes: number,
): Array<'ORDER' | 'TOO_SHORT' | 'OVERLAP' | null> {
  const sorted = windows.map((w, i) => ({ w, i })).sort((a, b) => a.w.startMinute - b.w.startMinute);
  const problems: Array<'ORDER' | 'TOO_SHORT' | 'OVERLAP' | null> = windows.map(() => null);
  sorted.forEach(({ w, i }, k) => {
    if (w.endMinute <= w.startMinute) problems[i] = 'ORDER';
    else if (w.endMinute - w.startMinute < durationMinutes) problems[i] = 'TOO_SHORT';
    else if (k > 0 && w.startMinute < sorted[k - 1].w.endMinute) problems[i] = 'OVERLAP';
  });
  return problems;
}
